import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  LuBold,
  LuCornerDownLeft,
  LuHeading1,
  LuHeading2,
  LuItalic,
  LuList,
  LuListOrdered,
  LuRedo2,
  LuStrikethrough,
  LuUndo2,
} from 'react-icons/lu';

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const btnClass = (active) => `p-1.5 rounded ${active ? 'bg-gray-300 text-black' : 'text-gray-600 hover:bg-gray-100'}`;

  return (
    <div className="flex flex-wrap gap-1 border-b border-gray-300 p-2">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={btnClass(editor.isActive('bold'))}
      >
        <LuBold size={16} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={btnClass(editor.isActive('italic'))}
      >
        <LuItalic size={16} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={btnClass(editor.isActive('strike'))}
      >
        <LuStrikethrough size={16} />
      </button>
      <div className="w-px bg-gray-300 mx-1" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={btnClass(editor.isActive('heading', { level: 1 }))}
      >
        <LuHeading1 size={16} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={btnClass(editor.isActive('heading', { level: 2 }))}
      >
        <LuHeading2 size={16} />
      </button>
      <div className="w-px bg-gray-300 mx-1" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={btnClass(editor.isActive('bulletList'))}
      >
        <LuList size={16} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={btnClass(editor.isActive('orderedList'))}
      >
        <LuListOrdered size={16} />
      </button>
      <div className="w-px bg-gray-300 mx-1" />
      <button type="button" onClick={() => editor.chain().focus().setHardBreak().run()} className={btnClass(false)}>
        <LuCornerDownLeft size={16} />
      </button>
      <div className="w-px bg-gray-300 mx-1" />
      <button
        type="button"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className={btnClass(false)}
      >
        <LuUndo2 size={16} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className={btnClass(false)}
      >
        <LuRedo2 size={16} />
      </button>
    </div>
  );
};

const TiptapEditor = ({ content, onUpdate, editable = true }) => {
  const parsedContent = (() => {
    if (!content) return '';
    if (typeof content === 'object') return content;
    try {
      return JSON.parse(content);
    } catch {
      return content;
    }
  })();

  const editor = useEditor({
    extensions: [StarterKit],
    content: parsedContent,
    editable,
    onCreate: ({ editor }) => {
      onUpdate({
        json: JSON.stringify(editor.getJSON()),
        text: editor.getText(),
      });
    },
    onUpdate: ({ editor }) => {
      onUpdate({
        json: JSON.stringify(editor.getJSON()),
        text: editor.getText(),
      });
    },
  });

  return (
    <div className={editable ? 'border border-gray-300' : ''}>
      {editable && <MenuBar editor={editor} />}
      <EditorContent
        editor={editor}
        className="tiptap-editor prose prose-sm max-w-none p-3 min-h-[200px] focus:outline-none"
      />
    </div>
  );
};

export default TiptapEditor;
